<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useFilters, useOrderExpenses } from "@/composables";
import OmniTable from "@/components/omni/OmniTable.vue";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import AddOrderExpenseDialog from "@/views/orders/view/components/AddOrderExpenseDialog.vue";
import EditOrderExpenseDialog from "@/views/orders/view/components/EditOrderExpenseDialog.vue";
import DeleteOrderExpenseDialog from "@/views/orders/view/components/DeleteOrderExpenseDialog.vue";

import type { OrderDto, OrderExpense } from "@/api/orders/dto/order.dto";

const { t } = useI18n();
const { mdash } = useFilters();
const { formatOrderExpenseRate } = useOrderExpenses();

const props = defineProps<{
  order: OrderDto;
}>();

const emits = defineEmits<{
  (e: "success-create-expense", expense: OrderExpense): void;
  (e: "success-edit-expense", expense: OrderExpense): void;
  (e: "success-delete-expense", expense: OrderExpense): void;
}>();

const isAddExpenseDialogOpen = ref(false);
const isEditExpenseDialogOpen = ref(false);
const editingExpense = ref<OrderExpense | null>(null);
const isDeleteExpenseDialogOpen = ref(false);
const deletingExpense = ref<OrderExpense | null>(null);

const headers = [
  { title: t("payments.type"), key: "type", sortable: false, width: 250 },
  { title: t("payments.note"), key: "note", sortable: false },
  {
    title: t("payments.affects-dispatcher-profit"),
    key: "affectsDispatcherProfit",
    sortable: false,
    align: "center",
    width: 200,
  },
  { title: t("payments.rate"), key: "rate", sortable: false, align: "end", width: 110 },
  { title: "", key: "actions", sortable: false, width: 84 },
];

const expenses = computed<OrderExpense[]>(() => {
  return props.order.expenses ?? [];
});

const rows = computed(() => {
  return expenses.value.map((expense) => {
    return {
      key: String(expense.id),
      expense,
      type: t(`order-expenses.${expense.type}`),
      note: mdash(expense.note ?? null),
      affectsDispatcherProfit: expense.affectsDispatcherProfit,
      rate: formatOrderExpenseRate(expense.rate),
    };
  });
});

const totalRate = computed<string>(() => {
  return formatOrderExpenseRate(expenses.value.reduce((total, expense) => total + expense.rate, 0));
});

function openEditExpenseDialog(expense: OrderExpense) {
  editingExpense.value = expense;
  isEditExpenseDialogOpen.value = true;
}

function openDeleteExpenseDialog(expense: OrderExpense) {
  deletingExpense.value = expense;
  isDeleteExpenseDialogOpen.value = true;
}
</script>

<template>
  <v-card class="order-expenses">
    <div class="d-flex align-center justify-space-between">
      <div class="order-expenses__title">{{ t("payments.expenses") }}</div>

      <permission-tooltip :permission="DispatcherPermissionValue.OrdersEdit">
        <template #activator="{ disabled }">
          <v-btn
            :disabled="disabled"
            class="order-expenses__add text-uppercase font-weight-600 font-size-12"
            color="primary"
            data-qa="order-expenses-add"
            variant="outlined"
            @click="isAddExpenseDialogOpen = true"
          >
            <template #prepend>
              <v-icon size="16">mdi-plus</v-icon>
            </template>
            {{ t("payments.add-expense") }}
          </v-btn>
        </template>

        {{ t("permission.edit-order") }}
      </permission-tooltip>
    </div>

    <omni-table
      :headers="headers"
      :hover="false"
      :items="rows"
      :items-per-page="-1"
      :server-data="false"
      class="order-expenses__table mt-6"
      hide-default-footer
      item-value="key"
    >
      <template #no-data>
        <span class="text-grey-darken-1 font-size-14">{{ t("payments.no-expenses") }}</span>
      </template>

      <template #[`item.affectsDispatcherProfit`]="{ item }">
        <v-icon v-if="item.affectsDispatcherProfit" color="success" size="24">mdi-check</v-icon>
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="d-flex align-center col-gap-3">
          <permission-tooltip :permission="DispatcherPermissionValue.OrdersEdit">
            <template #activator="{ disabled }">
              <v-btn
                :disabled="disabled"
                color="primary"
                data-qa="order-expense-edit"
                density="compact"
                icon="mdi-pencil-outline"
                size="20"
                variant="text"
                @click="openEditExpenseDialog(item.expense)"
              />
            </template>

            {{ t("permission.edit-order") }}
          </permission-tooltip>
          <permission-tooltip :permission="DispatcherPermissionValue.OrdersEdit">
            <template #activator="{ disabled }">
              <v-btn
                :disabled="disabled"
                color="error"
                data-qa="order-expense-delete"
                density="compact"
                icon="mdi-delete-outline"
                size="20"
                variant="text"
                @click="openDeleteExpenseDialog(item.expense)"
              />
            </template>

            {{ t("permission.edit-order") }}
          </permission-tooltip>
        </div>
      </template>
    </omni-table>

    <div class="d-flex justify-end order-expenses__total">
      <span>{{ t("payments.total-expenses") }}:</span>
      <span data-qa="order-expenses-total">{{ totalRate }}</span>
    </div>

    <add-order-expense-dialog
      v-model="isAddExpenseDialogOpen"
      :order-id="props.order.id!"
      @success="emits('success-create-expense', $event)"
    />

    <edit-order-expense-dialog
      v-model="isEditExpenseDialogOpen"
      :expense="editingExpense"
      :order-id="props.order.id!"
      @success="emits('success-edit-expense', $event)"
    />

    <delete-order-expense-dialog
      v-model="isDeleteExpenseDialogOpen"
      :expense="deletingExpense"
      :order-id="props.order.id!"
      @success="emits('success-delete-expense', $event)"
    />
  </v-card>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.order-expenses {
  padding: 16px;
  border-radius: 4px;
  background-color: $white;
  box-shadow: 0 4px 4px -1px rgba(0, 0, 0, 0.16);

  &__title {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.24;
    color: $grey-navy;
  }

  :deep(.order-expenses__table) {
    table {
      border: 1px solid $grey-lighter !important;
    }
  }

  &__total {
    gap: 12px;
    padding-right: 16px;
    font-size: 13px;
    font-weight: 600;
    color: $grey-navy;
  }
}
</style>
